import Link from '@lichtblick/link';
import classNames from 'classnames';
import type { HTMLAttributeAnchorTarget, JSX, MouseEventHandler } from 'react';
import type {} from '../../react';
import themeClass from '../../theme/theme.module.css';
import styles from './box-cta.module.css';
import { BoxCTAContent } from './box-cta-content';

type BoxCTABaseBaseProps = {
  className?: string;
  'data-dd-action-name'?: string;
  'data-testid'?: string;
  size?: 'large' | 'medium' | 'small';
  tabIndex?: number;
  /** @deprecated The 'theme' prop is no longer used; instead, set the photon theme class on the relevant parent element. */
  theme?: 'light' | 'dark';
  variant?: 'primary' | 'destructive' | 'neutral-filled' | 'neutral-tonal' | 'neutral-outline';
  wide?: boolean;
};

type BoxCTACommandProps =
  | {
      command?: never;
      commandFor?: never;
    }
  | {
      command: NonNullable<React.ButtonHTMLAttributes<HTMLButtonElement>['command']>;
      commandFor: NonNullable<React.ButtonHTMLAttributes<HTMLButtonElement>['commandfor']>;
      type: 'button';
    };

type BoxCTAWithChildrenProps = BoxCTABaseBaseProps & {
  ariaLabel?: string;
  children: React.ReactNode;
  leftSlot?: JSX.Element;
  rightSlot?: JSX.Element;
};
type BoxCTAWithoutChildrenProps = BoxCTABaseBaseProps & {
  ariaLabel: string;
  leftSlot: JSX.Element;
};
type BoxCTABaseProps = BoxCTAWithChildrenProps | BoxCTAWithoutChildrenProps;

export type BoxCTAButtonProps = BoxCTACommandProps &
  BoxCTABaseProps & {
    as: 'button';
    disabled?: boolean;
    formId?: string;
    loading?: boolean;
    loadingLabel?: string;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    type?: React.ButtonHTMLAttributes<undefined>['type'];
  };

export type BoxCTAAnchorProps = BoxCTABaseProps & {
  as: 'a';
  className?: string;
  download?: boolean;
  href: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  target?: HTMLAttributeAnchorTarget;
};

export type BoxCTAProps = BoxCTAButtonProps | BoxCTAAnchorProps;

export const BoxCTA: React.FC<BoxCTAProps> = (props) => {
  const {
    ariaLabel,
    as,
    className,
    'data-dd-action-name': ddAction,
    'data-testid': testId,
    leftSlot,
    size = 'large',
    tabIndex,
    theme = 'light',
    variant = 'primary',
    wide,
  } = props;
  const children = 'children' in props ? props.children : undefined;
  const rightSlot = 'rightSlot' in props ? props.rightSlot : undefined;
  const ctaClasses = classNames(
    'box-cta',
    styles.cta,
    styles[variant],
    styles[size],
    wide && styles.wide,
    theme === 'dark' && themeClass['dark-scheme'],
    className,
  );

  if (as === 'a') {
    const { download = false, href, onClick, target } = props;

    return (
      <Link
        aria-label={ariaLabel}
        className={ctaClasses}
        data-dd-action-name={ddAction}
        data-testid={testId}
        download={download}
        href={href}
        onClick={onClick}
        tabIndex={tabIndex}
        target={target}
      >
        <BoxCTAContent leftSlot={leftSlot} rightSlot={rightSlot}>
          {children}
        </BoxCTAContent>
      </Link>
    );
  }

  const { disabled, formId, loading, loadingLabel, onClick, type, command, commandFor } = props;

  return (
    <button
      aria-label={ariaLabel}
      className={classNames(ctaClasses, loading && styles.loading)}
      data-dd-action-name={ddAction}
      data-testid={testId}
      disabled={disabled || loading}
      form={formId}
      onClick={onClick}
      tabIndex={tabIndex}
      type={type}
      command={command}
      commandfor={commandFor}
    >
      <BoxCTAContent leftSlot={leftSlot} rightSlot={rightSlot} loading={loading} loadingLabel={loadingLabel}>
        {children}
      </BoxCTAContent>
    </button>
  );
};
